import { makeOutboundCall } from '../cxone-sdk.service';
import { logger } from '../../utils/logger';
import { normalizePhoneForDial } from '../../utils/format-phone.util';
import { OUTBOUND_DIAL_EVENT, OutboundDialEventDetail } from './types';

const DIAL_DEBOUNCE_MS = 1500;

let inFlightDial: Promise<void> | null = null;
let lastDialAt = 0;
let lastDialPhone: string | null = null;

export function emitOutboundDialEvent(phone: string, contactId?: string): void {
  const event = new CustomEvent<OutboundDialEventDetail>(OUTBOUND_DIAL_EVENT, {
    detail: { phone, contactId },
  });
  window.dispatchEvent(event);
  logger.debug('[SAP Context] Emitted outbound dial event:', { phone, contactId });
}

/**
 * Single-flight + short debounce for click-to-dial.
 * Duplicate SAP CTI messages (iframe + globals) often fire twice within ~1s.
 */
export function performOutboundDial(
  phoneNumber: string,
  options?: { skillId?: string | number; contactId?: string }
): Promise<void> {
  const dialPhoneNumber = normalizePhoneForDial(phoneNumber);
  if (!dialPhoneNumber) {
    return Promise.reject(new Error('Missing phone number'));
  }

  const now = Date.now();
  if (inFlightDial || (lastDialPhone === dialPhoneNumber && now - lastDialAt < DIAL_DEBOUNCE_MS)) {
    logger.debug('[SAP Context] Ignoring duplicate outbound dial', {
      phone: dialPhoneNumber,
      inFlight: Boolean(inFlightDial),
    });
    return inFlightDial ?? Promise.resolve();
  }

  lastDialAt = now;
  lastDialPhone = dialPhoneNumber;
  emitOutboundDialEvent(dialPhoneNumber, options?.contactId);

  const skillId =
    options?.skillId != null && options.skillId !== '' ? Number(options.skillId) : undefined;

  inFlightDial = makeOutboundCall(dialPhoneNumber, Number.isFinite(skillId) ? skillId : undefined)
    .then(() => {
      logger.debug('[SAP Context] Outbound dial completed');
    })
    .catch((err) => {
      logger.error('[SAP Context] Outbound dial failed:', err);
      throw err;
    })
    .finally(() => {
      inFlightDial = null;
    });

  return inFlightDial;
}

/** Test helper — resets dial single-flight state. */
export function resetOutboundDialGuard(): void {
  inFlightDial = null;
  lastDialAt = 0;
  lastDialPhone = null;
}
